import * as React from 'react';
import { useMachine } from "@xstate/react";
import { createFlowMachine } from './machine';
import { FlowMachineContext, FlowEvents, FlowMachineSchema, FlowMachineTypeState } from './types';
import { Interpreter, State } from 'xstate';
import Flow from '../../model/Flow';
import { FlowWindow } from '../utils/globals';
import { indexFromUrl } from '../utils/commonFunctions';
import { string_id } from '../utils/getInfo';
import log from "loglevel"
import * as Sentry from "@sentry/browser"

declare let window: FlowWindow;

type TypeState = Distribute<keyof FlowMachineSchema["states"], FlowMachineContext> // assuming same context
/**
 * Type defining possible values that can be passed to `send()`
 */
export type Send = Interpreter<FlowMachineContext, FlowMachineSchema, FlowEvents, TypeState>['send']

/**
 * Type defining our xstate context.
 */
export type Context = [
    State<FlowMachineContext, FlowEvents, FlowMachineSchema, FlowMachineTypeState>,
    Send,
    Interpreter<FlowMachineContext, FlowMachineSchema, FlowEvents, FlowMachineTypeState>
];

type Distribute<U, C> = U extends any ? { value: U; context: C } : never // util


export const MachineContext = React.createContext<Context>({} as any);

// Debug: Log flow initialization source
const flowSource = typeof window.flow != "undefined" ? 'window.flow' : 'Flow.default()';
const flow =
    typeof window.flow != "undefined" ? Flow.fromJSON(window.flow) : Flow.default()
console.log(`[useMachine] Flow initialized from ${flowSource}, screens: ${flow.screens.length}`,
    flow.screens.map((s, i) => ({
        index: i,
        id: s.id?.substring(0, 8),
        mmoCount: s.mixedMediaObjects?.length || 0
    }))
);
let index = indexFromUrl()
if (index > flow.screens.length - 1) {
    window.history.pushState({}, "screen", "?screen=1");
    index = 0
}

/**
 * Wrapper component hiding the setup of the xstate machine
 */
export const FlowMachine: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let location = `/s/${string_id}`;
    const machine = useMachine(createFlowMachine({
        flow,
        index,
        undoStack: [],
        redoStack: [],
        loading: [],
        drafts: [],
        currentObject: null,
        publishConfig: { asDraft: true, forceToView: false, viewLocation: location, count: 0, total: 0 },
        isInteracting: false,
    }), { devTools: true });

    const [, , service] = machine;

    // Set parentService in context after machine is created
    React.useEffect(() => {
        service.send({ type: 'SET_PARENT_SERVICE', service });
    }, [service]);

    // Removed heavy Sentry logging that was causing memory leaks
    // JSON.stringify on entire context (with base64 images) on every state transition
    // was creating huge strings and filling Sentry breadcrumb buffer

    return (
        <MachineContext.Provider value={machine}>
            {children}
        </MachineContext.Provider>
    )
}

export default FlowMachine;